import React, { Fragment } from 'react';

import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { FormGroup } from 'reactstrap';

import { Field, reduxForm, Form, formValueSelector } from 'redux-form';

import { toastr } from 'react-redux-toastr';

import FormItem from '../../../common/forms/FormItem';

import TextInput from '../../../common/forms/input/TextInput';

import DynamicForm from '../../../common/forms/DynamicForm';

import FormFooter from '../../../common/forms/FormFooter';

import Loader from '../../../common/Loader';

import { CURRENCY } from '../../../../config/locale';

import { numberFormatter } from '../../../../utils/number';

import { BANK_ACCOUNT_COLUMNS } from '../../../../config/dynamicFormFields';

// Ajustar import según tu proyecto
import AgencySelect from '../../../common/forms/select/AgencySelect';
import DatePicker from '../../../common/forms/input/DatePicker';

const FORM_NAME = 'PrivateServiceDepositForm';

const selector = formValueSelector(FORM_NAME);

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalizedValue = String(value).trim().replace(',', '.');

  const numberValue = Number(normalizedValue);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const getSelectId = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'object') {
    return value.value !== null && value.value !== undefined
      ? Number(value.value)
      : null;
  }

  return Number(value);
};

const hasRegularDepositData = (deposit) => {
  if (!deposit) {
    return false;
  }

  return (
    getSelectId(deposit.bankAccountId) !== null ||
    Boolean(deposit.operationNumber) ||
    parseAmount(deposit.actualAmountDeposited) > 0 ||
    Boolean(deposit.depositDate)
  );
};

const AmountDisplay = ({ value }) => (
  <div className="input-group">
    <input
      type="text"
      className="form-control"
      value={parseAmount(value).toFixed(2)}
      disabled
      readOnly
    />

    <div className="input-group-append">
      <span className="input-group-text">{CURRENCY}</span>
    </div>
  </div>
);

AmountDisplay.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

AmountDisplay.defaultProps = {
  value: 0,
};

const validateContractDeposits = (values) => {
  const errors = {};

  const contractDepositList = Array.isArray(values.contractDepositList)
    ? values.contractDepositList.filter(hasRegularDepositData)
    : [];

  const hasAgencyPayment = Boolean(values.isAgencyPayment);

  if (contractDepositList.length === 0 && !hasAgencyPayment) {
    errors.contractDepositListElements = 'Debe ingresar al menos un depósito';
  }

  if (hasAgencyPayment) {
    const agencyDeposit = values.agencyDeposit || {};

    const agencyDepositErrors = {};

    if (!agencyDeposit.agencyId) {
      agencyDepositErrors.agencyId = 'Debe seleccionar una agencia';
    }

    if (!agencyDeposit.operationNumber) {
      agencyDepositErrors.operationNumber =
        'Debe ingresar el número de operación';
    }

    if (parseAmount(agencyDeposit.actualAmountDeposited) <= 0) {
      agencyDepositErrors.actualAmountDeposited =
        'El monto depositado debe ser mayor a cero';
    }

    if (!agencyDeposit.depositDate) {
      agencyDepositErrors.depositDate = 'Debe ingresar la fecha de depósito';
    }

    if (Object.keys(agencyDepositErrors).length > 0) {
      errors.agencyDeposit = agencyDepositErrors;
    }
  }

  return errors;
};

const PrivateServiceDepositForm = ({
  handleSubmit,
  handleProcess,
  loading,
  totalAmount,
  totalDeposited,
  remainingAmount,
  batchTotal,
  isAgencyPayment,
}) => {
  const handleContractDeposits = async (formValues) => {
    const {
      contractDepositList = [],
      agencyDeposit = {},
      isAgencyPayment: agencyPayment,
    } = formValues;

    const regularDeposits = Array.isArray(contractDepositList)
      ? contractDepositList.filter(hasRegularDepositData)
      : [];

    if (regularDeposits.length === 0 && !agencyPayment) {
      toastr.error('Error', 'Debe ingresar al menos un depósito');

      return;
    }

    if (agencyPayment) {
      if (!agencyDeposit.agencyId) {
        toastr.error('Error', 'Debe seleccionar una agencia');

        return;
      }

      if (!agencyDeposit.operationNumber) {
        toastr.error(
          'Error',
          'Debe ingresar el número de operación de la agencia',
        );

        return;
      }

      if (parseAmount(agencyDeposit.actualAmountDeposited) <= 0) {
        toastr.error('Error', 'El monto de agencia debe ser mayor a cero');

        return;
      }

      if (!agencyDeposit.depositDate) {
        toastr.error('Error', 'Debe ingresar la fecha del depósito de agencia');

        return;
      }
    }

    const deposits = regularDeposits.map((deposit) => ({
      bankAccountId: getSelectId(deposit.bankAccountId),

      operationNumber: deposit.operationNumber,

      actualAmountDeposited: parseAmount(deposit.actualAmountDeposited),

      depositDate: deposit.depositDate,

      depositDifferenceAmount:
        deposit.depositDifferenceAmount !== null &&
        deposit.depositDifferenceAmount !== undefined &&
        deposit.depositDifferenceAmount !== ''
          ? parseAmount(deposit.depositDifferenceAmount)
          : null,

      depositDifferenceReasonId:
        deposit.depositDifferenceReasonId !== null &&
        deposit.depositDifferenceReasonId !== undefined &&
        deposit.depositDifferenceReasonId !== ''
          ? Number(deposit.depositDifferenceReasonId)
          : null,
    }));

    if (agencyPayment) {
      deposits.push({
        agencyId: getSelectId(agencyDeposit.agencyId),

        operationNumber: agencyDeposit.operationNumber,

        actualAmountDeposited: parseAmount(agencyDeposit.actualAmountDeposited),

        depositDate: agencyDeposit.depositDate,

        depositDifferenceAmount: null,

        depositDifferenceReasonId: null,
      });
    }

    const currentBatchTotal = deposits.reduce(
      (total, deposit) => total + parseAmount(deposit.actualAmountDeposited),
      0,
    );

    if (currentBatchTotal <= 0) {
      toastr.error('Error', 'El monto a depositar debe ser mayor a cero');

      return;
    }

    const currentRemainingAmount = parseAmount(remainingAmount);

    if (currentBatchTotal > currentRemainingAmount) {
      toastr.error(
        'Error',
        `El monto ingresado supera el saldo pendiente de ${numberFormatter({
          value: currentRemainingAmount,
        })}`,
      );

      return;
    }

    await handleProcess({
      contractDepositList: deposits,
    });
  };

  if (loading) {
    return <Loader />;
  }

  const numericTotalAmount = parseAmount(totalAmount);

  const numericTotalDeposited = parseAmount(totalDeposited);

  const numericRemainingAmount = parseAmount(remainingAmount);

  const numericBatchTotal = parseAmount(batchTotal);

  const remainingAfterDeposit = Math.max(
    numericRemainingAmount - numericBatchTotal,
    0,
  );

  const contractFullyPaid =
    numericTotalAmount > 0 && numericRemainingAmount <= 0;

  const exceedsRemainingAmount = numericBatchTotal > numericRemainingAmount;

  const willBeFullyPaid =
    numericBatchTotal > 0 &&
    Math.abs(numericBatchTotal - numericRemainingAmount) < 0.01;

  return (
    <Form onSubmit={handleSubmit(handleContractDeposits)}>
      <Fragment>
        <h3>Depósito de Contrato</h3>

        <FormGroup row>
          <FormItem label="Monto Total del Contrato">
            <AmountDisplay value={numericTotalAmount} />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Total Depositado">
            <AmountDisplay value={numericTotalDeposited} />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Saldo Pendiente">
            <AmountDisplay value={numericRemainingAmount} />
          </FormItem>
        </FormGroup>

        <hr />

        {contractFullyPaid ? (
          <div className="alert alert-success">
            El contrato se encuentra completamente pagado.
          </div>
        ) : (
          <Fragment>
            <h3>Origen del Pago</h3>

            <FormGroup row>
              <FormItem label="Pago por Agencia">
                <Field
                  name="isAgencyPayment"
                  component="input"
                  type="checkbox"
                />
              </FormItem>
            </FormGroup>

            {isAgencyPayment && (
              <Fragment>
                <h4>Depósito por Agencia</h4>

                <FormGroup row>
                  <FormItem label="Agencia">
                    <Field
                      name="agencyDeposit.agencyId"
                      component={AgencySelect}
                    />
                  </FormItem>

                  <FormItem label="Número de Operación">
                    <Field
                      name="agencyDeposit.operationNumber"
                      component={TextInput}
                      type="text"
                    />
                  </FormItem>

                  <FormItem label="Monto depositado">
                    <Field
                      name="agencyDeposit.actualAmountDeposited"
                      component={TextInput}
                    />
                  </FormItem>

                  <FormItem label="Fecha de depósito">
                    <Field
                      name="agencyDeposit.depositDate"
                      component={DatePicker}
                      time
                    />
                  </FormItem>
                </FormGroup>
              </Fragment>
            )}

            <hr />

            <h3>Depósitos Bancarios</h3>

            <DynamicForm
              name="contractDepositList"
              columns={BANK_ACCOUNT_COLUMNS}
            />

            <Field
              name="contractDepositListElements"
              component={TextInput}
              type="hidden"
            />

            <br />

            <FormGroup row>
              <FormItem label="Total del Depósito">
                <AmountDisplay value={numericBatchTotal} />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Saldo después del Depósito">
                <AmountDisplay value={remainingAfterDeposit} />
              </FormItem>
            </FormGroup>

            {exceedsRemainingAmount && (
              <div className="alert alert-danger">
                El total ingresado supera el saldo pendiente del contrato.
              </div>
            )}

            {willBeFullyPaid && (
              <div className="alert alert-success">
                Con este depósito el contrato quedará completamente pagado.
              </div>
            )}

            <FormFooter />
          </Fragment>
        )}
      </Fragment>
    </Form>
  );
};

PrivateServiceDepositForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,

  handleProcess: PropTypes.func.isRequired,

  loading: PropTypes.bool,

  totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  totalDeposited: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  remainingAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  batchTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  isAgencyPayment: PropTypes.bool,
};

PrivateServiceDepositForm.defaultProps = {
  loading: false,

  totalAmount: 0,

  totalDeposited: 0,

  remainingAmount: 0,

  batchTotal: 0,

  isAgencyPayment: false,
};

const mapStateToProps = (state, ownProps) => {
  const { totalAmount = 0, totalDeposited = 0 } = ownProps;

  const contractDepositList = selector(state, 'contractDepositList') || [];

  const isAgencyPayment = selector(state, 'isAgencyPayment') || false;

  const agencyDeposit = selector(state, 'agencyDeposit') || {};

  const regularDepositsTotal = Array.isArray(contractDepositList)
    ? contractDepositList.reduce((total, deposit) => {
        if (!deposit) {
          return total;
        }

        return total + parseAmount(deposit.actualAmountDeposited);
      }, 0)
    : 0;

  const agencyDepositTotal = isAgencyPayment
    ? parseAmount(agencyDeposit.actualAmountDeposited)
    : 0;

  const batchTotal = regularDepositsTotal + agencyDepositTotal;

  const numericTotalAmount = parseAmount(totalAmount);

  const numericTotalDeposited = parseAmount(totalDeposited);

  const remainingAmount = Math.max(
    numericTotalAmount - numericTotalDeposited,
    0,
  );

  return {
    totalAmount: numericTotalAmount,

    totalDeposited: numericTotalDeposited,

    remainingAmount,

    batchTotal,

    isAgencyPayment,
  };
};

const formComponent = reduxForm({
  form: FORM_NAME,

  validate: validateContractDeposits,

  initialValues: {
    isAgencyPayment: false,
  },
})(PrivateServiceDepositForm);

export default connect(mapStateToProps)(formComponent);
